import { yupResolver } from "@hookform/resolvers/yup";
import { Box, type DrawerProps, Grid, IconButton, Stack, Typography, styled, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer, DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as ArrowDownIcon } from "Components/_Shared/Icons/Assets/arrow-down.svg";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { Popper } from "Components/_Shared/Popper";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { FormTextarea } from "Forms/Components/FormTextarea";
import { type InsuranceContractFormType, getInsuranceContractFormSchema } from "Forms/Schemas/Insurance/insuranceContractFormSchema";
import {
  getListInsuranceContractsQueryKey,
  useCreateInsuranceContract,
  useDeleteInsuranceContract,
  useListAnalyzerPartners,
  useListAssetContractExpenseOptions,
  useUpdateInsuranceContract,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type Contract, ContractContractType, ListAnalyzerPartnersToolType } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { LOAD_VALUES_EXPENSE_TYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { getAmountFrequencyTranslation } from "Infrastructure/Utils/LanguageUtils";
import { type FC, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";

type Props = {
  readonly onClose: () => void;
  readonly analysisId: string;
  readonly versionId: string;
  readonly clientId: string;
  readonly insuranceContractType: ContractContractType;
  readonly insuranceContract?: Contract;
} & DrawerProps;

const StyledItem = styled(Stack)`
  width: 300px;
  &:hover {
    cursor: pointer;
    background: rgba(0, 0, 0, 0.03);
  }
`;

export const InsuranceContractDrawer: FC<Props> = ({
  onClose: propsOnClose,
  analysisId,
  versionId,
  clientId,
  insuranceContract,
  insuranceContractType,
  open,
  ...rest
}) => {
  const { t } = useResource();
  const { readOnly } = useContext(AnalysisDetailContext);
  const queryClient = useQueryClient();
  const theme = useTheme();

  const [expenseOptionsDropdownOpen, setExpenseOptionsDropdownOpen] = useState(false);

  const { data: insurancePartnersData } = useListAnalyzerPartners({ toolType: ListAnalyzerPartnersToolType.insurance });
  const { data: expenseOptions } = useListAssetContractExpenseOptions(analysisId, versionId);

  const { mutateAsync: createInsuranceContract } = useCreateInsuranceContract({
    mutation: {
      onSuccess: () => {
        toast.success(t(Resources.AnalysisDetail.Insurance.InsuranceContractCreated));
        queryClient.invalidateQueries({ queryKey: getListInsuranceContractsQueryKey(analysisId, versionId, clientId) });
        onClose();
      },
    },
  });

  const { mutateAsync: updateInsuranceContract } = useUpdateInsuranceContract({
    mutation: {
      onSuccess: () => {
        toast.success(t(Resources.AnalysisDetail.Insurance.InsuranceContractEdited));
        queryClient.invalidateQueries({ queryKey: getListInsuranceContractsQueryKey(analysisId, versionId, clientId) });
        onClose();
      },
    },
  });

  const { mutateAsync: deleteInsuranceContract } = useDeleteInsuranceContract({
    mutation: {
      onSuccess: () => {
        toast.success(t(Resources.AnalysisDetail.Insurance.InsuranceContractDeleted));
        queryClient.invalidateQueries({ queryKey: getListInsuranceContractsQueryKey(analysisId, versionId, clientId) });
        onClose();
      },
    },
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
  } = useForm<InsuranceContractFormType>({
    resolver: yupResolver(getInsuranceContractFormSchema(t)),
    mode: "onBlur",
  });

  const onClose = () => {
    propsOnClose();
    setExpenseOptionsDropdownOpen(false);
  };

  const onSubmit = async (data: InsuranceContractFormType) => {
    try {
      const payload = {
        partnerId: data.partnerId ? Number(data.partnerId) : null,
        createRecommended: !!insuranceContract?.createRecommended,
        insuranceAmount: {
          currencyCode: "CZK",
          frequency: data.frequency as AmountFrequency,
          value: data.insuranceAmount,
        },
        contractType: insuranceContract ? insuranceContract.contractType : insuranceContractType,
        note: data.note || "",
        product: data.productName || "",
      };

      if (!insuranceContract) {
        await createInsuranceContract({
          analysisId,
          versionId,
          clientGuid: clientId,
          data: payload,
        });
      } else {
        await updateInsuranceContract({
          analysisId,
          versionId,
          clientGuid: clientId,
          contractGuid: insuranceContract.guid,
          data: payload,
        });
      }
    } catch {
      toast.error(t(Resources.Common.Error));
    }
  };

  const onDelete = async () => {
    if (!insuranceContract) return;
    try {
      await deleteInsuranceContract({ analysisId, versionId, clientGuid: clientId, contractGuid: insuranceContract.guid });
    } catch {
      toast.error(t(Resources.Common.Error));
    }
  };

  const insurancePartners: Option[] =
    insurancePartnersData?.map((partner) => ({
      value: partner.id.toString(),
      name: partner.name,
    })) || [];

  const frequencies: Option[] = [
    { value: AmountFrequency.one_time, name: t(Resources.Common.OneTime) },
    { value: AmountFrequency.monthly, name: t(Resources.Common.Monthly) },
    { value: AmountFrequency.quarterly, name: t(Resources.Common.Quarterly) },
    { value: AmountFrequency.half_yearly, name: t(Resources.Common.HalfYearly) },
    { value: AmountFrequency.yearly, name: t(Resources.Common.Yearly) },
  ];

  // biome-ignore lint: <Open is required.>
  useEffect(() => {
    const defaultValues = {
      partnerId: insuranceContract?.partnerId?.toString(),
      frequency: insuranceContract?.insuranceAmount.frequency ?? AmountFrequency.monthly,
      insuranceAmount: insuranceContract?.insuranceAmount.value.toString() ?? ("0" as any),
      note: insuranceContract?.note,
      productName: insuranceContract?.product,
    };
    reset(defaultValues);
  }, [insuranceContract, open, reset]);

  return (
    <Drawer anchor="right" open={open} {...rest} onClose={onClose}>
      <Box display="flex" justifyContent="space-between" alignItems="center" p={Spacing.XXL}>
        <Typography variant="h2">
          {insuranceContractType === ContractContractType.current
            ? t(Resources.AnalysisDetail.Insurance.ExistingSolution)
            : t(Resources.AnalysisDetail.Insurance.RecommendedSolution)}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DrawerContent>
        <Stack spacing={Spacing.XL} p={Spacing.XXL}>
          <Grid container spacing={Spacing.XL}>
            {expenseOptions && expenseOptions.length > 0 && (
              <Grid size={12}>
                <Popper
                  placement="bottom-start"
                  isOpen={expenseOptionsDropdownOpen}
                  onOpen={() => {
                    setExpenseOptionsDropdownOpen(true);
                  }}
                  btnEl={
                    <Button
                      variant="text"
                      disabled={readOnly}
                      colorCustom={theme.colors.brandPrimary}
                      sx={{ width: "fit-content", marginLeft: -Spacing.M }}
                      endAdornment={<ArrowDownIcon />}
                    >
                      {t(Resources.AnalysisDetail.Insurance.LoadFromExistingExpenses)}
                    </Button>
                  }
                >
                  <Stack spacing={Spacing.M} paddingY={Spacing.S} display="flex" justifyContent="space-between">
                    {expenseOptions?.map((option, index) => (
                      <StyledItem
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                          paddingX: Spacing.M,
                          paddingY: Spacing.S,
                          borderRadius: Radius.S,
                        }}
                        key={`${option.expenseType}-${index}`}
                        onClick={() => {
                          setValue("insuranceAmount", option.amount?.value.toString() as any);
                          if (option.amount?.frequency) setValue("frequency", option.amount.frequency);
                          setExpenseOptionsDropdownOpen(false);
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {t(
                              Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.InsuranceExpenseTypes[
                                LOAD_VALUES_EXPENSE_TYPE_RESOURCE_KEY[option.expenseType]
                              ],
                            )}
                          </Typography>
                          <Typography variant="caption" color="secondary" fontWeight={500} mb={Spacing.M}>
                            {option.institution}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="secondary" fontWeight={500}>
                          {formatCurrency(option.amount?.value ?? 0, option.amount?.currencyCode ?? "CZK")}/
                          {option.amount?.frequency ? getAmountFrequencyTranslation(option.amount.frequency, t) : ""}
                        </Typography>
                      </StyledItem>
                    ))}
                  </Stack>
                </Popper>
              </Grid>
            )}
            <Grid size={6}>
              <FormDropdown
                name="partnerId"
                options={insurancePartners}
                control={control}
                disabled={readOnly}
                label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Partner)}
                labelEndAdornment={<StyledLabelAdornment />}
                errors={errors}
              />
            </Grid>
            <Grid size={6}>
              <BlFormInput
                name="productName"
                disabled={readOnly}
                control={control}
                label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Product)}
                labelEndAdornment={<StyledLabelAdornment />}
                errors={errors}
              />
            </Grid>
            <Grid size={6}>
              <BlFormInput
                disabled={readOnly}
                control={control}
                name="insuranceAmount"
                label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.InsuranceAmount)}
                errors={errors}
                mask={[
                  {
                    mask: Number,
                    thousandsSeparator: " ",
                    scale: 0,
                    min: 0,
                  },
                ]}
                inputEndAdornment={
                  <Typography variant="body2" color="secondary">
                    Kč
                  </Typography>
                }
              />
            </Grid>
            <Grid size={6}>
              <FormDropdown
                name="frequency"
                options={frequencies}
                control={control}
                disabled={readOnly}
                label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Frequency)}
                errors={errors}
              />
            </Grid>
            <Grid size={12}>
              <FormTextarea
                disabled={readOnly}
                control={control}
                name="note"
                label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                errors={errors}
              />
            </Grid>
          </Grid>
        </Stack>
      </DrawerContent>
      <Box display="flex" alignItems="center" justifyContent="space-between" py={Spacing.XXL} px={Spacing.XXL}>
        <Box>
          {!!insuranceContract && (
            <Button
              variant="text"
              color="error"
              disabled={readOnly}
              sx={{
                marginRight: Spacing.XL,
              }}
              onClick={onDelete}
            >
              {t(Resources.Common.Delete)}
            </Button>
          )}
        </Box>
        <Box display="flex" flexDirection="row" justifyContent="flex-end">
          <Button
            onClick={onClose}
            color="secondary"
            sx={{
              marginRight: Spacing.XL,
            }}
          >
            {t(Resources.Common.Close)}
          </Button>
          <Button disabled={readOnly} type="submit" onClick={() => handleSubmit(onSubmit)()}>
            {insuranceContract ? t(Resources.Common.Edit) : t(Resources.Common.Create)}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
